import { useState, useEffect } from 'react';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      applyConsent(savedPreferences);
    }
  }, []);

  const applyConsent = (prefs: CookiePreferences) => {
    // Activer/désactiver Google Analytics
    if (prefs.analytics) {
      window.gtag?.('consent', 'update', {
        analytics_storage: 'granted',
      });
    } else {
      window.gtag?.('consent', 'update', {
        analytics_storage: 'denied',
      });
    }

    // Activer/désactiver les cookies marketing
    if (prefs.marketing) {
      window.gtag?.('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      });
    } else {
      window.gtag?.('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    applyConsent(allAccepted);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    applyConsent(onlyNecessary);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    applyConsent(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleTogglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Les cookies nécessaires ne peuvent pas être désactivés
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) {
    return (
      <button
        onClick={() => setShowBanner(true)}
        className="fixed bottom-6 left-6 z-50 bg-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
        aria-label="Gérer les cookies"
      >
        <i className="ri-shield-check-line text-2xl text-teal-600 group-hover:scale-110 transition-transform"></i>
      </button>
    );
  }

  return (
    <>
      {/* Banner principale */}
      {!showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl animate-slide-up">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                    <i className="ri-cookie-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Nous respectons votre vie privée
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Nous utilisons des cookies pour améliorer votre expérience, analyser notre trafic et personnaliser le contenu. 
                      Vous pouvez accepter tous les cookies ou personnaliser vos préférences. 
                      <button
                        onClick={() => setShowSettings(true)}
                        className="text-teal-600 hover:text-teal-700 font-medium ml-1 cursor-pointer whitespace-nowrap"
                      >
                        En savoir plus
                      </button>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleRejectAll}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Refuser tout
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-6 py-3 text-sm font-medium text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Personnaliser
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-3 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Accepter tout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panneau de paramètres détaillés */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg">
                    <i className="ri-settings-3-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Paramètres des cookies</h2>
                    <p className="text-sm text-gray-600 mt-1">Gérez vos préférences de confidentialité</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  aria-label="Fermer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-8 py-6 space-y-6">
              {/* Cookie nécessaires */}
              <div className="border border-gray-200 rounded-xl p-6 bg-gray-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-shield-check-line text-xl text-teal-600"></i>
                      <h3 className="text-lg font-bold text-gray-900">Cookies nécessaires</h3>
                      <span className="px-3 py-1 text-xs font-medium text-teal-700 bg-teal-100 rounded-full whitespace-nowrap">
                        Toujours actifs
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Ces cookies sont essentiels au fonctionnement du site et ne peuvent pas être désactivés. 
                      Ils permettent des fonctionnalités de base comme la navigation sécurisée et l'accès aux zones protégées.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-6 bg-teal-600 rounded-full relative cursor-not-allowed opacity-50">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cookies analytiques */}
              <div className="border border-gray-200 rounded-xl p-6 hover:border-teal-200 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-bar-chart-line text-xl text-blue-600"></i>
                      <h3 className="text-lg font-bold text-gray-900">Cookies analytiques</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">
                      Ces cookies nous aident à comprendre comment les visiteurs interagissent avec notre site en collectant 
                      des informations anonymes sur les pages visitées et les erreurs rencontrées.
                    </p>
                    <p className="text-xs text-gray-500">
                      <strong className="text-gray-700">Services utilisés :</strong> Google Analytics
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handleTogglePreference('analytics')}
                      className={`w-12 h-6 rounded-full relative transition-colors cursor-pointer ${
                        preferences.analytics ? 'bg-teal-600' : 'bg-gray-300'
                      }`}
                      aria-label="Activer/désactiver les cookies analytiques"
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.analytics ? 'right-1' : 'left-1'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Cookies marketing */}
              <div className="border border-gray-200 rounded-xl p-6 hover:border-teal-200 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-megaphone-line text-xl text-purple-600"></i>
                      <h3 className="text-lg font-bold text-gray-900">Cookies marketing</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">
                      Ces cookies sont utilisés pour vous proposer des publicités pertinentes et mesurer l'efficacité 
                      de nos campagnes marketing. Ils peuvent être définis par nos partenaires publicitaires.
                    </p>
                    <p className="text-xs text-gray-500">
                      <strong className="text-gray-700">Services utilisés :</strong> Google Ads, Facebook Pixel, LinkedIn Insight
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handleTogglePreference('marketing')}
                      className={`w-12 h-6 rounded-full relative transition-colors cursor-pointer ${
                        preferences.marketing ? 'bg-teal-600' : 'bg-gray-300'
                      }`}
                      aria-label="Activer/désactiver les cookies marketing"
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.marketing ? 'right-1' : 'left-1'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Info RGPD */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-xl text-blue-600 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <h4 className="text-sm font-bold text-blue-900 mb-2">Vos droits RGPD</h4>
                    <p className="text-sm text-blue-800 leading-relaxed">
                      Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression de vos données. 
                      Vous pouvez modifier vos préférences à tout moment en cliquant sur l'icône des cookies en bas à gauche de la page.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-8 py-6 rounded-b-2xl">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <a
                  href="/confidentialite"
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
                >
                  Politique de confidentialité
                </a>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleRejectAll}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Refuser tout
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="px-6 py-3 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Enregistrer mes choix
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
